// Re-export the shared GameSignals singleton from the framework so that
// internal framework modules (ModulesManager, etc.) and game code share
// the exact same signal instances.
import { GameSignals as FrameworkGameSignals } from '@dragonframework/modules'
import { Signal } from 'typed-signals'

const GameSignals = FrameworkGameSignals as any

GameSignals.holdAndWinStartSpin ??= new Signal<() => void>()
GameSignals.holdAndWinStopSpin ??= new Signal<(data: any) => void>()
GameSignals.holdAndWinStop ??= new Signal<() => void>()
GameSignals.holdAndWinBoardSpinStarted ??= new Signal<(respinsLeft: number) => void>()
GameSignals.holdAndWinStickyStopped ??= new Signal<(cellIndex: number) => void>()
GameSignals.holdAndWinRespinsCounterUpdated ??= new Signal<(respinsLeft: number) => void>()
GameSignals.holdAndWinCoinLanded ??= new Signal<(cellIndex: number) => void>()
//## Collect fly SFX: value leaves the coin → hits the ticker box.
GameSignals.holdAndWinCollectFlyStart ??= new Signal<() => void>()
GameSignals.holdAndWinCollectFlyHit ??= new Signal<() => void>()
GameSignals.holdAndWinFullBoardReached ??= new Signal<
    (data: { lockedCount: number; totalCells: number; collectedTotalPreview: number }) => void
>()
GameSignals.holdAndWinFullBoardCleared ??= new Signal<() => void>()
GameSignals.baseGameCoinLanded ??= new Signal<
    (data: {
        reelIndex: number
        multiplier: number
        coinType?: string
        symbolKey?: string
        symbolIndex: number
    }) => void
>()
GameSignals.holdAndWinTransitionStart ??= new Signal<() => void>()
GameSignals.holdAndWinCollectComplete ??= new Signal<() => void>()
GameSignals.holdAndWinTriggerCoinWinStart ??= new Signal<() => void>()
GameSignals.holdAndWinTriggerCoinWinComplete ??= new Signal<() => void>()
GameSignals.fsCollectSequenceRequested ??= new Signal<() => void>()
GameSignals.fsCollectSequenceComplete ??= new Signal<() => void>()
GameSignals.fsCollectSequenceSkip ??= new Signal<() => void>()
GameSignals.fsCollectSequenceForceComplete ??= new Signal<() => void>()
GameSignals.fsCoinCollectMoveToLadder ??= new Signal<() => void>()
GameSignals.featureIntroRequested ??= new Signal<
    (data: { kind: 'freespins' | 'holdandwin'; freeSpinsCount?: number; retrigger?: boolean }) => void
>()
GameSignals.featureIntroDisplayRequested ??= new Signal<
    (data: { kind: 'freespins' | 'holdandwin'; freeSpinsCount?: number; retrigger?: boolean }) => void
>()
GameSignals.featureIntroSkipConfirmed ??= new Signal<() => void>()
GameSignals.featureIntroContinue ??= new Signal<() => void>()
GameSignals.bigWinClosing ??= new Signal<() => void>()
//## Commendation count-up: one start per level, one complete for the whole climb (counterLoop / counterEnd).
GameSignals.bigWinCounterStart ??= new Signal<() => void>()
GameSignals.bigWinCounterComplete ??= new Signal<() => void>()
//## Fired the instant the player dismisses a feature popup — before the box's.
GameSignals.featurePopupDismissStarted ??= new Signal<() => void>()
GameSignals.lineByLineWinStep ??= new Signal<(combination: any) => void>()
GameSignals.buyBonusOpenButtonClicked ??= new Signal<() => void>()
GameSignals.buyBonusConfirmButtonClicked ??= new Signal<() => void>()
/** Absolute Win Meter sync (major units). Use after H&W collect / restore — not a delta. */
GameSignals.setWinPanelValue ??= new Signal<(value: number) => void>()
/** Raise Win Meter to at least this absolute value (major units); never decrease. */
GameSignals.setWinPanelValueAtLeast ??= new Signal<(value: number) => void>()

//## H&W Values Countup at the end of spin..
GameSignals.holdAndWinCollectProgress ??= new Signal<(total: number) => void>()

//## Payline loop in the game area: one emit per line drawn, so the ticker can follow it.
GameSignals.lineByLineStepChanged ??= new Signal<(data: { combination: any }) => void>()
/** Between paylines: board has no win animations / no active payline graphic. */
GameSignals.lineByLinePause ??= new Signal<() => void>()
GameSignals.lineByLineLoopStopped ??= new Signal<() => void>()
/** Settings / menu UI opened — interrupt looping line-win presentation (same as stake change). */
GameSignals.settingsMenuOpened ??= new Signal<() => void>()

//## Jackpot Box Win Animation in H&W.
GameSignals.holdAndWinCollectStarted ??= new Signal<() => void>()
GameSignals.holdAndWinJackpotSymbolLanded ??= new Signal<(tier: 'grand' | 'major' | 'minor' | 'mini') => void>()
//## Fired every time a jackpot coin finishes landing in an H&W cell (SFX).
GameSignals.holdAndWinJackpotCoinLanded ??= new Signal<(tier: 'grand' | 'major' | 'minor' | 'mini') => void>()

//## Will see this H&W and Free spin..!
GameSignals.jackpotPopupStart ??= new Signal<
    (
        requests: Array<
            'mini' | 'minor' | 'major' | 'grand' | { tier: 'mini' | 'minor' | 'major' | 'grand'; amount?: string }
        >
    ) => void
>()
GameSignals.jackpotPopupComplete ??= new Signal<() => void>()
//## Fired when each individual Jackpot Popup starts, so the ticker can show its tier (+ amount).
GameSignals.jackpotPopupTierShown ??= new Signal<
    (
        payload: 'mini' | 'minor' | 'major' | 'grand' | { tier: 'mini' | 'minor' | 'major' | 'grand'; amount?: string }
    ) => void
>()

//## Jackpot Upgrade presentation, shown mid FS coin collect when a ladder milestone is reached.
//## Carries the index of the ladder level that was just completed.
GameSignals.jackpotLadderUpgradeStart ??= new Signal<(levelIndex: number) => void>()
GameSignals.jackpotLadderUpgradeComplete ??= new Signal<() => void>()
//## Player dismissed the upgrade early; cuts the hold short and plays the out clip.
GameSignals.jackpotLadderUpgradeSkip ??= new Signal<() => void>()

//## Server-capped round (current_round.misc_prizes.capamount_reached). The popup runs
//## after the feature end popups; the round waits on maxExposureComplete before closing.
GameSignals.showMaxExposure ??= new Signal<() => void>()
GameSignals.maxExposureComplete ??= new Signal<() => void>()
//## Fired the instant the player dismisses it, before the box's out clip — so the texts
//## fade out with the box instead of hard-cutting (same shape as featurePopupDismissStarted).
GameSignals.maxExposureDismissStarted ??= new Signal<() => void>()
//## Box finished its in clip. Texts reveal on this rather than a hardcoded delay, so they
//## stay in step if the clip length changes.
GameSignals.maxExposureBoxOpened ??= new Signal<() => void>()

GameSignals.unfinishedRoundRequested ??= new Signal<() => void>()
GameSignals.unfinishedRoundContinue ??= new Signal<() => void>()

//## Uninfinished title fade in effect.
GameSignals.unfinishedRoundBoxOpened ??= new Signal<() => void>()
GameSignals.unfinishedRoundDismissStarted ??= new Signal<() => void>()

//## Total win Sound Signal.
GameSignals.showTotalWinLabel ??= new Signal<(totalWin: number) => void>()

//## The player actually pressed Stop (panel button or space bar), as opposed to the generic
//## stopButtonClicked, which every skip path raises — including a tap anywhere on the screen,
//## and twice for a single panel press. Sounds tied to the press itself belong here.
GameSignals.spinStopPressed ??= new Signal<() => void>()

export default GameSignals
